'use client';

import { useState } from 'react';
import { Shift } from '@/lib/types';
import { formatDateDisplay } from '@/lib/date-utils';
import { icsSummary } from '@/lib/ics-generator';

interface Props {
  shifts: Shift[];
  onShiftsChange: (shifts: Shift[]) => void;
  format: 'mmc' | 'casey' | 'vhh';
}

function localDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatTime(shift: Shift): string {
  if (shift.type !== 'shift' || !shift.startTime) return 'All day';
  return `${shift.startTime} - ${shift.endTime}${shift.isOvernight ? ' +1' : ''}`;
}

function parseTime(text: string): { startTime: string; endTime: string; isOvernight: boolean } | null {
  const m = text.match(/^(\d{1,4})\s*-\s*(\d{1,4})(?:\s*\+(\d+))?$/);
  if (!m) return null;
  return {
    startTime: m[1].padStart(4, '0'),
    endTime: m[2].padStart(4, '0'),
    isOvernight: m[3] !== undefined,
  };
}

function mondayOf(date: Date): number {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function isNewWeek(prev: Date, curr: Date): boolean {
  if (!prev) return false;
  return mondayOf(curr) > mondayOf(prev);
}

const TYPE_COLORS: Record<Shift['type'], string> = {
  shift: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  leave: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  other: 'bg-zinc-100 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-300',
};
const TYPE_ORDER: Shift['type'][] = ['shift', 'leave', 'other'];

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    </svg>
  );
}

function TickIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export default function ShiftTable({ shifts, onShiftsChange, format }: Props) {
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editBuf, setEditBuf] = useState<Shift | null>(null);

  if (shifts.length === 0) {
    return <p className="text-zinc-500">No shifts found for this person.</p>;
  }

  const startEdit = (index: number) => {
    setEditingIdx(index);
    setEditBuf({ ...shifts[index] });
  };

  const cancelEdit = () => {
    setEditingIdx(null);
    setEditBuf(null);
  };

  const confirmEdit = () => {
    if (editingIdx === null || !editBuf) return;
    const updated = shifts.map((s, i) => (i === editingIdx ? { ...editBuf } : s));
    onShiftsChange(updated);
    setEditingIdx(null);
    setEditBuf(null);
  };

  const deleteShift = (index: number) => {
    if (editingIdx !== null) return;
    const updated = shifts.filter((_, i) => i !== index);
    onShiftsChange(updated);
  };

  const updateBuf = (field: keyof Shift, value: string | boolean | Date) => {
    if (!editBuf) return;
    const copy = { ...editBuf };
    if (field === 'date' && typeof value === 'string') {
      copy.date = new Date(value + 'T00:00:00');
    } else if (field === 'type') {
      copy.type = value as Shift['type'];
    } else if (field === 'isOvernight') {
      copy.isOvernight = value as boolean;
    } else {
      (copy as any)[field] = value;
    }
    setEditBuf(copy);
  };

  const handleTimeEdit = (text: string) => {
    const parsed = parseTime(text);
    if (parsed && editBuf) {
      setEditBuf({
        ...editBuf,
        startTime: parsed.startTime,
        endTime: parsed.endTime,
        isOvernight: parsed.isOvernight,
        type: 'shift',
      });
    }
  };

  const cycleEditType = () => {
    if (!editBuf) return;
    const idx = TYPE_ORDER.indexOf(editBuf.type);
    const next = TYPE_ORDER[(idx + 1) % TYPE_ORDER.length];
    updateBuf('type', next);
  };

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full min-w-[650px] text-sm">
        <thead>
          <tr className="border-b border-zinc-200 dark:border-zinc-700">
            <th className="text-left py-2 pr-3 text-zinc-600 dark:text-zinc-400 whitespace-nowrap">Date</th>
            <th className="text-left py-2 pr-3 text-zinc-600 dark:text-zinc-400">Day</th>
            <th className="text-left py-2 pr-3 text-zinc-600 dark:text-zinc-400">Time</th>
            <th className="text-left py-2 pr-3 text-zinc-600 dark:text-zinc-400">Shift</th>
            <th className="text-left py-2 pr-3 text-zinc-600 dark:text-zinc-400">Role</th>
            <th className="text-left py-2 pr-3 text-zinc-600 dark:text-zinc-400">Type</th>
            <th className="text-left py-2 text-zinc-600 dark:text-zinc-400 w-10" />
          </tr>
        </thead>
        <tbody>
          {shifts.map((shift, i) => {
            const isEditing = editingIdx === i;
            const buf = isEditing ? editBuf! : shift;

            return (
              <tr key={i} className={i > 0 && isNewWeek(shifts[i - 1].date, shift.date) ? 'border-t-4 border-zinc-400 dark:border-zinc-400 border-b border-zinc-100 dark:border-zinc-800' : 'border-b border-zinc-100 dark:border-zinc-800'}>
                <td className="py-2 pr-3 text-zinc-800 dark:text-zinc-200">
                  {isEditing ? (
                    <input
                      type="date"
                      value={localDateStr(buf.date)}
                      onChange={e => updateBuf('date', e.target.value)}
                      className="w-[130px] bg-transparent text-zinc-800 dark:text-zinc-200 border border-zinc-300 dark:border-zinc-600 rounded px-1.5 py-0.5 focus:outline-none focus:border-zinc-500"
                    />
                  ) : (
                    formatDateDisplay(shift.date)
                  )}
                </td>
                <td className="py-2 pr-3 text-zinc-500 dark:text-zinc-400 whitespace-nowrap">
                  {shift.date.toLocaleDateString('en-AU', { weekday: 'short' })}
                </td>
                <td className="py-2 pr-3 text-zinc-800 dark:text-zinc-200 whitespace-nowrap">
                  {isEditing ? (
                    <input
                      type="text"
                      defaultValue={formatTime(buf)}
                      onBlur={e => {
                        const v = e.target.value.trim();
                        if (v && v !== formatTime(buf)) handleTimeEdit(v);
                        else e.target.value = formatTime(buf);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        if (e.key === 'Escape') {
                          (e.target as HTMLInputElement).value = formatTime(buf);
                          (e.target as HTMLInputElement).blur();
                        }
                      }}
                      className="w-[140px] bg-transparent text-zinc-800 dark:text-zinc-200 border border-zinc-300 dark:border-zinc-600 rounded px-1.5 py-0.5 focus:outline-none focus:border-zinc-500 font-mono text-xs"
                    />
                  ) : shift.type === 'shift' && shift.startTime ? (
                    <>{shift.startTime} - {shift.endTime}{shift.isOvernight ? ' +1' : ''}</>
                  ) : (
                    <span className="text-zinc-400">All day</span>
                  )}
                </td>
                <td className="py-2 pr-3 text-zinc-800 dark:text-zinc-200">
                  {isEditing ? (
                    <input
                      type="text"
                      value={buf.label}
                      onChange={e => updateBuf('label', e.target.value)}
                      className="w-28 bg-transparent text-zinc-800 dark:text-zinc-200 border border-zinc-300 dark:border-zinc-600 rounded px-1.5 py-0.5 focus:outline-none focus:border-zinc-500 text-xs"
                    />
                  ) : (
                    icsSummary(shift, format)
                  )}
                </td>
                <td className="py-2 pr-3 text-zinc-600 dark:text-zinc-400">
                  {isEditing ? (
                    <input
                      type="text"
                      value={buf.location}
                      onChange={e => updateBuf('location', e.target.value)}
                      className="w-28 bg-transparent text-zinc-800 dark:text-zinc-200 border border-zinc-300 dark:border-zinc-600 rounded px-1.5 py-0.5 focus:outline-none focus:border-zinc-500 text-xs"
                    />
                  ) : (
                    shift.location
                  )}
                </td>
                <td className="py-2 pr-3">
                  {isEditing ? (
                    <button
                      onClick={cycleEditType}
                      className={`px-2 py-0.5 rounded text-xs font-medium cursor-pointer border-0 ${TYPE_COLORS[buf.type]}`}
                      title="Click to cycle: shift → leave → other"
                    >
                      {buf.type}
                    </button>
                  ) : (
                    <span className={`px-2 py-0.5 rounded text-xs ${TYPE_COLORS[shift.type]}`}>
                      {shift.type}
                    </span>
                  )}
                </td>
                <td className="py-2">
                  {isEditing ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={confirmEdit}
                        className="p-1 rounded text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950 transition-colors"
                        aria-label="Confirm changes"
                      >
                        <TickIcon />
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
                        aria-label="Cancel changes"
                      >
                        <CrossIcon />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => startEdit(i)}
                        className="p-1 rounded text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                        aria-label="Edit shift"
                      >
                        <PencilIcon />
                      </button>
                      <button
                        onClick={() => deleteShift(i)}
                        className="p-1 rounded text-zinc-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
                        aria-label="Delete shift"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}